/**
 * Dashboard Page
 */

import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as ActiveIcon,
  Schedule as TrialIcon,
  Block as SuspendedIcon,
} from '@mui/icons-material';
import { usePlatformStats } from '../hooks';
import { format } from 'date-fns';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  isLoading?: boolean;
}

function StatCard({ title, value, icon, color, isLoading }: StatCardProps) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {isLoading ? (
              <Skeleton width={60} height={40} />
            ) : (
              <Typography variant="h4" fontWeight={600}>
                {value}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${color}20`,
              color: color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const { data: stats, isLoading } = usePlatformStats();

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Platform overview and statistics
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Tenants"
            value={stats?.totalTenants ?? 0}
            icon={<BusinessIcon fontSize="large" />}
            color="#1976d2"
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Active Subscriptions"
            value={stats?.activeSubscriptions ?? 0}
            icon={<ActiveIcon fontSize="large" />}
            color="#2e7d32"
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Trial Subscriptions"
            value={stats?.trialSubscriptions ?? 0}
            icon={<TrialIcon fontSize="large" />}
            color="#ed6c02"
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Suspended"
            value={stats?.suspendedTenants ?? 0}
            icon={<SuspendedIcon fontSize="large" />}
            color="#d32f2f"
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Tenants
              </Typography>
              {isLoading ? (
                <Box>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height={60} sx={{ my: 1 }} />
                  ))}
                </Box>
              ) : stats?.recentTenants && stats.recentTenants.length > 0 ? (
                <List disablePadding>
                  {stats.recentTenants.map((tenant) => (
                    <ListItem key={tenant.id} divider sx={{ px: 0 }}>
                      <ListItemText
                        primary={tenant.name}
                        secondary={tenant.slug}
                      />
                      <Chip
                        label={format(new Date(tenant.createdAt), 'dd.MM.yyyy')}
                        size="small"
                        variant="outlined"
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recent tenants
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Use the sidebar navigation to manage tenants, modules, feature flags, and platform users.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
